import { orderService } from '@/services/order.service';
import { downloadBlob } from '@/utils/downloadBlob';
import type { TenantOrder } from '@/mocks/orders';
import { buildOrderCustomerDocData, renderOrderCustomerDocHtml } from './orderCustomerDoc';

/**
 * TCR-1962 — "Gửi cho khách": tải tài liệu "ĐƠN HÀNG" về máy dưới 3 định dạng
 * để người dùng tự gửi qua Zalo/email.
 *
 * - `image`: chụp TOÀN BỘ tài liệu thành 1 PNG dài (yêu cầu ticket khi nhiều trang).
 * - `pdf`: cùng ảnh chụp đó cắt thành trang A4. Chữ trong PDF là ảnh — đổi lại
 *   tiếng Việt luôn đúng mà không phải nhúng font Unicode vào jsPDF.
 * - `word`: `.docx` thật (thư viện `docx`) — mở được bằng Word, Pages, Google Docs,
 *   sửa lại được trước khi gửi. Xem `orderCustomerDocx.ts`.
 */
export type OrderCustomerDocFormat = 'pdf' | 'image' | 'word';

/** Rộng A4 ở 96dpi — khớp `width: 794px` của `.tcr-order-doc`. */
const A4_WIDTH_PX = 794;
const A4_HEIGHT_PX = Math.round((A4_WIDTH_PX * 297) / 210);
/** Chụp gấp đôi để ảnh/PDF không nhoè khi khách phóng to trên điện thoại. */
const CAPTURE_SCALE = 2;

/**
 * Lấy chi tiết đơn (dòng bảng danh sách thiếu `service_lines`, `buyer_invoice_info`
 * đầy đủ...) rồi gộp với dòng bảng. Tên phường/tỉnh CHỈ có trên response danh
 * sách (`GET /orders` $lookup) — mapper chi tiết trả `undefined` cho 2 field đó,
 * spread thẳng sẽ ghi đè mất, nên giữ lại giá trị của dòng bảng.
 *
 * PUBLIC (TCR-2506) — dùng lại cho luồng in tài liệu ĐƠN HÀNG của đơn sỉ.
 */
export async function loadOrder(row: TenantOrder): Promise<TenantOrder> {
  if (!row._id) return row;
  const detail = await orderService.getDetail(row._id);
  if (!detail) return row;
  return {
    ...row,
    ...detail,
    items: detail.items?.length ? detail.items : row.items,
    shipping_city_name: detail.shipping_city_name ?? row.shipping_city_name,
    shipping_ward_name: detail.shipping_ward_name ?? row.shipping_ward_name,
  };
}

/** Render HTML vào 1 node ngoài màn hình, trả node gốc `.tcr-order-doc` + hàm dọn. */
function mountOffscreen(html: string): { root: HTMLElement; cleanup: () => void } {
  const host = document.createElement('div');
  host.style.cssText = 'position:fixed;left:-10000px;top:0;z-index:-1;';
  host.innerHTML = html;
  document.body.appendChild(host);
  const root = host.querySelector<HTMLElement>('.tcr-order-doc');
  if (!root) {
    host.remove();
    throw new Error('Không dựng được tài liệu đơn hàng');
  }
  return { root, cleanup: () => host.remove() };
}

async function capture(root: HTMLElement): Promise<HTMLCanvasElement> {
  const { default: html2canvas } = await import('html2canvas');
  await document.fonts?.ready;
  return html2canvas(root, { scale: CAPTURE_SCALE, backgroundColor: '#ffffff', logging: false });
}

/**
 * Điểm được phép ngắt trang (px CSS, tính từ đỉnh tài liệu): đáy từng dòng bảng
 * và từng khối. Ngắt đúng các điểm này để không cắt đôi một dòng chữ giữa 2 trang.
 */
function collectBreakPoints(root: HTMLElement): number[] {
  const top = root.getBoundingClientRect().top;
  const points = Array.from(root.querySelectorAll('tr, p, h1'))
    .map((el) => Math.round(el.getBoundingClientRect().bottom - top));
  return [...new Set(points)].sort((a, b) => a - b);
}

/** Chọn vị trí ngắt từng trang: điểm xa nhất còn vừa trang, không có thì cắt cứng. */
export function planPageBreaks(totalHeight: number, pageHeight: number, breakPoints: number[]): number[] {
  const ends: number[] = [];
  let start = 0;
  while (totalHeight - start > pageHeight) {
    const limit = start + pageHeight;
    const candidate = breakPoints.filter((p) => p > start && p <= limit).pop();
    const end = candidate && candidate - start > pageHeight / 2 ? candidate : limit;
    ends.push(end);
    start = end;
  }
  ends.push(totalHeight);
  return ends;
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Không tạo được ảnh'))), 'image/png');
  });
}

async function buildPdf(canvas: HTMLCanvasElement, breakPoints: number[]): Promise<Blob> {
  const { default: jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const mmPerPx = 210 / A4_WIDTH_PX;
  const totalHeight = canvas.height / CAPTURE_SCALE;
  let start = 0;
  planPageBreaks(totalHeight, A4_HEIGHT_PX, breakPoints).forEach((end, index) => {
    const slice = document.createElement('canvas');
    slice.width = canvas.width;
    slice.height = Math.round((end - start) * CAPTURE_SCALE);
    const ctx = slice.getContext('2d');
    if (ctx) {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, slice.width, slice.height);
      ctx.drawImage(canvas, 0, -Math.round(start * CAPTURE_SCALE));
    }
    if (index > 0) pdf.addPage();
    pdf.addImage(slice.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, 210, (end - start) * mmPerPx);
    start = end;
  });
  return pdf.output('blob');
}

export async function exportOrderCustomerDoc(row: TenantOrder, format: OrderCustomerDocFormat): Promise<void> {
  const order = await loadOrder(row);
  const data = buildOrderCustomerDocData(order);
  const baseName = `DonHang_${order.order_number}`;

  if (format === 'word') {
    const { buildOrderCustomerDocx } = await import('./orderCustomerDocx');
    downloadBlob(await buildOrderCustomerDocx(data), `${baseName}.docx`);
    return;
  }

  const { root, cleanup } = mountOffscreen(renderOrderCustomerDocHtml(data));
  try {
    const canvas = await capture(root);
    if (format === 'image') {
      downloadBlob(await canvasToBlob(canvas), `${baseName}.png`);
    } else {
      downloadBlob(await buildPdf(canvas, collectBreakPoints(root)), `${baseName}.pdf`);
    }
  } finally {
    cleanup();
  }
}

/**
 * TCR-2506 — tải file .docx của tài liệu "ĐƠN HÀNG" với TÊN FILE do caller
 * đặt (đơn sỉ GT/MT dùng tên `CRM_Đơn hàng_<giờ>_<mã đơn>.docx` theo mẫu
 * khách). Khác `exportOrderCustomerDoc(row, 'word')` vốn luôn đặt tên
 * `DonHang_<mã>.docx` cho luồng "Gửi cho khách" — tên đó giữ nguyên.
 */
export async function exportOrderCustomerDocx(row: TenantOrder, fileName: string): Promise<void> {
  const order = await loadOrder(row);
  const data = buildOrderCustomerDocData(order);
  const { buildOrderCustomerDocx } = await import('./orderCustomerDocx');
  downloadBlob(await buildOrderCustomerDocx(data), fileName);
}
